import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import { useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
  where,
} from 'firebase/firestore';
import { useUser } from 'reactfire';
import CircularProgress from '@mui/material/CircularProgress';
import useGlobal from 'global-state/store';
import { useFormik } from 'formik';
import OurPagination from 'components/OurPagination';
import {
  Button, Menu, MenuItem, Snackbar, Alert,
} from '@mui/material';
import { httpsCallable, getFunctions } from 'firebase/functions';
import ScannedCertificateListHeaders from './ScannedCertificateListHeaders';
import ScannedCertificateListItem from './ScannedCertificateListItem';

export default function ScannedCertificatesList() {
  const { t } = useTranslation();
  const analytics = getAnalytics();
  const { data: user } = useUser();
  const [globalState] = useGlobal();
  const navigate = useNavigate();
  const [orderBy, setOrderBy] = useState('parser.processedAt');
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const functions = getFunctions();
  functions.region = 'europe-west1';

  const computeListOfConditions = useCallback((formikValues) => {
    const conditions = [];

    const addInCondition = (key, value, all) => {
      if (value && value !== '' && value !== '-') {
        conditions.push(where(key, 'in', [value]));
      } else {
        conditions.push(where(key, 'in', all));
      }
    };

    addInCondition('certificateType', formikValues.type, ['tanker_truck', 'gauged_container']);

    // Include all statuses: ready, error, processing
    addInCondition('status', formikValues.status, ['ready', 'error', 'processing']);

    return conditions;
  }, []);

  const formikInitValues = useMemo(() => ({
    type: '',
    status: '',
    orderBy: 'parser.processedAt',
  }), []);

  const [listOfConditions, setListOfConditions] = useState(computeListOfConditions(formikInitValues));

  useEffect(() => {
    if (globalState.activeOrganization !== '' && user?.uid) {
      logEvent(analytics, 'loading_scanned_certificates', {
        user_uid: user?.uid,
        appName: 'Digitank, Gauger',
        organization: globalState.activeOrganization,
      });
    }
  }, [analytics, globalState.activeOrganization, user?.uid]);

  const handleScanDocument = (type) => {
    navigate('/certificates/scan-pdf', {
      state: {
        certificateType: type,
      },
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteScan = async (scan, refreshCallback) => {
    try {
      const deleteScannedCertificate = httpsCallable(functions, 'deleteScannedCertificateCall2ndGen');

      await deleteScannedCertificate({
        scanId: scan.id,
        organizationId: globalState.activeOrganization,
      });

      setSnackbar({
        open: true,
        message: t('scanned_certificates.delete_success'),
        severity: 'success',
      });

      // Log the deletion event
      logEvent(analytics, 'scanned_certificate_deleted', {
        user_uid: user?.uid,
        appName: 'Digitank, Gauger',
        organization: globalState.activeOrganization,
        scanId: scan.id,
        originalFileName: scan.originalFileName,
        certificateType: scan.certificateType,
      });

      // Trigger refresh of the list
      if (refreshCallback) {
        refreshCallback(listOfConditions, orderBy);
      }
    } catch (error) {
      console.error('Error deleting scanned certificate:', error);
      setSnackbar({
        open: true,
        message: t('scanned_certificates.delete_error'),
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, mb: { xs: 8, md: 1 },
      }}
    >
      <Typography variant="h5" component="div" sx={{ mb: 2, mt: 2 }}>
        {t('scanned_certificates.title')}
      </Typography>
      {globalState.activeOrganization && (
      <OurPagination
        collectionPath={`organizations/${globalState.activeOrganization}/apps/digitank-gauger/scannedCertificates`}
        listOfConditions={listOfConditions}
        orderByAttribute={orderBy}
        filters={(
          <ScannedCertificateListFilters
            formikInitValues={formikInitValues}
            setListOfConditions={setListOfConditions}
            computeListOfConditions={computeListOfConditions}
            setOrderBy={setOrderBy}
          />
            )}
        list={(
          <ScannedCertificateList
            listOfConditions={listOfConditions}
            orderBy={orderBy}
            onDelete={handleDeleteScan}
            initSearch={(conditions, order) => {
              setListOfConditions(conditions);
              setOrderBy(order);
            }}
          />
            )}
      />
      )}
      {!globalState.activeOrganization && <CircularProgress />}

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1,
        position: 'fixed',
        bottom: '5%',
        right: '5%',
      }}
      >
        <Fab
          color="primary"
          aria-label="scan document"
          component={Button}
          onClick={handleMenuOpen}
          variant="extended"
        >
          <AddIcon />
          {t('scanned_certificates.scan_document')}
        </Fab>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleScanDocument('gauged_container');
              handleMenuClose();
            }}
          >
            {t('scanned_certificates.gauged_container')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleScanDocument('tanker_truck');
              handleMenuClose();
            }}
            disabled
          >
            {t('scanned_certificates.tanker_truck')}
          </MenuItem>
        </Menu>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function ScannedCertificateList({
  docs, initSearch, listOfConditions, orderBy, onDelete,
}) {
  const scans = docs.map((d) => ({ id: d.id, ...d.data() }));

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', width: '100%', gap: 1,
    }}
    >
      <ScannedCertificateListHeaders />
      <List
        dense
        sx={{
          width: '100%', border: 2, p: 0, borderRadius: '0 0 4px 4px', borderColor: 'white',
        }}
      >
        {scans.map((scan, i) => (
          <div key={scan.id}>
            <ScannedCertificateListItem
              index={i}
              alternateBackground={i % 2 === 0}
              scan={scan}
              onDelete={(scanToDelete) => onDelete(scanToDelete, initSearch)}
            />
          </div>
        ))}
      </List>
    </Box>
  );
}

function ScannedCertificateListFilters({
  initSearch, formikInitValues, setListOfConditions, computeListOfConditions, setOrderBy,
}) {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: formikInitValues,
    onSubmit: (values) => {
      const newConditions = computeListOfConditions(values);
      setListOfConditions(newConditions);
      setOrderBy(values.orderBy);
      initSearch(newConditions, values.orderBy);
    },
  });

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', width: '100%',
    }}
    >
      {/* Add filters here if needed */}
    </Box>
  );
}
